import { financeApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { landingPath, visibleModes, type AccessEvaluator } from "@bw/shared/navigation";
import type { FacilityServiceType } from "@bw/shared/types";

import {
  businessAccessEvaluator,
  businessNavigation,
  type BusinessNavAccess,
} from "@/config/modes";
import { useOrgFacilitiesQuery } from "@/app/FacilityContext";
import { useFinanceContext } from "@/hooks/use-finance-context";
import { useMyClubCoachProfile } from "@/hooks/use-my-club-coach-profile";
import { useMyFacilityActions } from "@/hooks/use-my-facility-actions";
import { useMyOrg } from "@/hooks/use-my-org";
import { useResolvedFacility } from "@/hooks/use-selected-facility";

export interface BusinessNavAccessState {
  allow: AccessEvaluator<BusinessNavAccess>;
  /** The same rules as if the organization were approved (route gate only). */
  allowWhenApproved: AccessEvaluator<BusinessNavAccess>;
  /**
   * The same rules with every failed visibility input assumed granted. A denial
   * this would lift is unconfirmed (route gate only).
   */
  allowIfFailedInputsGranted: AccessEvaluator<BusinessNavAccess>;
  /** False while the organization, facility or facility actions load. */
  ready: boolean;
  /** False while the org finance context loads (finance-gated sections wait on it). */
  financeReady: boolean;
  /** False while the club coach profile loads (My coaching waits on it). */
  coachReady: boolean;
  /**
   * A visibility input failed outright (facility actions, finance context or
   * coach profile; a missing coach profile is not a failure). Denials are
   * then unconfirmed, and `retryAccess` asks again.
   */
  accessCheckFailed: boolean;
  retryAccess: () => void;
  /** Exact ownership of the selected organization (loading fails closed). */
  organizationOwner: boolean;
  storageKey: string;
  /** Where this user lands (null until ready). */
  landing: string | null;
}

const COACH_ACTIONS = ["facility:coach", "facility:lesson_quote"];

/**
 * Whether the person coaches here: a coach action at the selected facility, or
 * a club coach profile in the organization (the coaching page's own signal).
 */
export function coachesHere(
  actions: readonly string[],
  coachProfileFound: boolean,
): boolean {
  return coachProfileFound || actions.some((action) => COACH_ACTIONS.includes(action));
}

/**
 * Per-mode memory namespace: one user at one organization and facility, so a
 * remembered page never carries across a workspace or facility switch.
 */
export function businessNavStorageKey(
  userId: string | null | undefined,
  orgId: string | null | undefined,
  facilityId: string | null | undefined,
): string {
  return ["business", userId ?? "anonymous", orgId ?? "no-org", facilityId ?? "no-facility"]
    .map(encodeURIComponent)
    .join(":");
}

/**
 * Home when visible. Without Home: a coach with no front-desk duty lands on
 * My coaching (their day); everyone else on their first menu page.
 */
function businessLanding(
  allow: AccessEvaluator<BusinessNavAccess>,
  actions: readonly string[],
  facilitySelected: boolean,
  organizationOwner: boolean,
): string | null {
  // Unscoped staff (no facility) would "see" every facility page; send them
  // to Inbox, which needs no facility. Owners keep their Home.
  if (!facilitySelected && !organizationOwner) return "/inquiries";
  const modes = visibleModes(businessNavigation, allow);
  const pages = modes.flatMap((mode) => mode.pages);
  if (pages.some((page) => page.id === businessNavigation.homePageId)) {
    return landingPath(businessNavigation, modes);
  }
  const coach = actions.some((action) => COACH_ACTIONS.includes(action));
  if (coach && !actions.includes("facility:checkin")) {
    const coaching = pages.find((page) => page.id === "my-coaching");
    if (coaching) return coaching.path;
  }
  return landingPath(businessNavigation, modes);
}

/**
 * The permission hints the business navigation reads: org status and
 * ownership, the org finance context, and the caller's action set at the
 * selected facility. Missing or loading hints fail closed; the server remains
 * authoritative on every page and endpoint.
 */
export function useBusinessNavAccess(): BusinessNavAccessState {
  const { user } = useAuth();
  const view = useMyOrg();
  const finance = useFinanceContext(
    view.org?.org_id,
    financeApi,
    view.org?.finance_capability === "allowed",
  );
  const orgStatus = view.org?.status;
  const organizationOwner = Boolean(
    user?.uid && view.org?.owner_user_id === user.uid,
  );

  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();
  const facilityActions = useMyFacilityActions(facilityId);
  const { actions } = facilityActions;
  // Settled once an answer arrived; a later refetch never makes it unknown again.
  const actionsLoading = !(facilityActions.isSettled ?? !facilityActions.isLoading);
  const scopeToActions = Boolean(facilityId) || facilityLoading;
  const allowedActions = facilityId && !actionsLoading ? actions : [];
  const financeRead =
    finance.context?.allowed_actions.includes("org:finance_read") === true;
  const canManageFinance = finance.context?.can_manage_finance_access === true;
  const canManageStaff = view.org?.can_manage_staff;
  // Coaches: a coach action here, or a club coach profile in the org (the
  // coaching page's own signal). A missing profile (404) or loading fails closed.
  const coachProfile = useMyClubCoachProfile(view.org?.org_id);
  const isCoach = coachesHere(
    allowedActions,
    coachProfile.isSuccess && Boolean(coachProfile.data),
  );
  const enabledServices = facility
    ? ((facility.enabled_services ?? []) as FacilityServiceType[])
    : null;

  // The shell auto-selects the first facility once the org's list arrives;
  // until then the action scope is unknown, so nothing is ready to show.
  const orgId = view.org?.org_id;
  const { data: orgFacilities, isLoading: facilitiesLoading } =
    useOrgFacilitiesQuery(orgId);
  const awaitingSelection =
    Boolean(orgId) &&
    (facilitiesLoading || (!facilityId && (orgFacilities?.length ?? 0) > 0));

  const ready =
    view.kind !== "loading" &&
    !awaitingSelection &&
    !facilityLoading &&
    !(Boolean(facilityId) && actionsLoading);

  const accessContext = {
    scopeToActions,
    actions: allowedActions,
    canManageStaff,
    financeRead,
    canManageFinance,
    organizationOwner,
    orgStatus,
    enabledServices,
    isCoach,
  };
  const allow = businessAccessEvaluator(accessContext);
  const allowWhenApproved = businessAccessEvaluator({ ...accessContext, orgStatus: "active" });
  const actionsFailed = Boolean(facilityId) && facilityActions.isError === true;
  const allowIfFailedInputsGranted = businessAccessEvaluator({
    ...accessContext,
    // Unscoped: action-gated items count as visible.
    scopeToActions: actionsFailed ? false : accessContext.scopeToActions,
    financeRead: financeRead || finance.query.isError,
    isCoach: isCoach || coachProfile.isError,
  });

  return {
    allow,
    allowWhenApproved,
    allowIfFailedInputsGranted,
    organizationOwner,
    ready,
    // Settled answers stay settled while they refetch (window focus, a newly
    // mounted reader), so the route gate never falls back to its spinner.
    financeReady: !finance.pending || finance.query.errorUpdateCount > 0,
    coachReady: !coachProfile.isPending || coachProfile.isFetched || coachProfile.fetchStatus === "idle",
    accessCheckFailed:
      actionsFailed ||
      finance.query.isError ||
      coachProfile.isError,
    retryAccess: () => {
      if (facilityActions.isError) facilityActions.refetch?.();
      if (finance.query.isError) void finance.query.refetch();
      if (coachProfile.isError) void coachProfile.refetch();
    },
    storageKey: businessNavStorageKey(user?.uid, orgId, facilityId),
    landing: ready
      ? businessLanding(
          allow,
          allowedActions,
          Boolean(facilityId),
          // Owner details are returned only to admitted owners and co-owners.
          organizationOwner || Boolean(view.org?.owner_user_id),
        )
      : null,
  };
}
